import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, Send } from 'lucide-react';

export default function Composer() {
  const [params] = useSearchParams();
  const [to, setTo] = useState(params.get('to') || '');
  const [phone, setPhone] = useState(params.get('phone') || '');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [fromName, setFromName] = useState('');
  const [gateways, setGateways] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  useEffect(() => {
    api.gateways().then((r) => setGateways(r.gateways || [])).catch(() => {});
    api.settings().then((r) => {
      if (r.settings?.default_from_name) setFromName(r.settings.default_from_name);
    }).catch(() => {});
  }, []);

  const charCount = body.length;
  const smsSegments = Math.ceil(charCount / 160) || 1;

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus(null);
    setLoading(true);
    try {
      const res = await api.sendSms({
        phone: phone || undefined,
        gatewayEmail: to || undefined,
        subject: subject || undefined,
        body,
        fromName: fromName || undefined,
      });
      if (res.success) {
        setStatus({ type: 'success', msg: `Sent successfully (id: ${res.messageId})` });
        setBody('');
      } else {
        setStatus({ type: 'error', msg: res.error || 'Send failed' });
      }
    } catch (err) {
      setStatus({ type: 'error', msg: err instanceof Error ? err.message : 'Send failed' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8 max-w-2xl">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Compose SMS via Email</h1>
        <p className="text-muted-foreground mt-1">
          Message is sent as email to the carrier’s email-to-SMS gateway
        </p>
      </div>

      <Card className="border-amber-500/40 bg-amber-500/5">
        <CardContent className="pt-4 text-sm text-muted-foreground">
          Many gateways are shut down or filtered. Delivery is best-effort. Prefer short messages
          (≤160 chars). Subject is often ignored by gateways.
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">New message</CardTitle>
          <CardDescription>
            To field accepts a full gateway address (e.g. 5551234567@vtext.com) or leave blank and
            provide a phone that was looked up previously.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSend} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="to">To (gateway email)</Label>
              <Input
                id="to"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                placeholder="5551234567@vtext.com"
                className="font-mono"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone (if no gateway yet)</Label>
              <Input
                id="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+15551234567"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="fromName">From name</Label>
              <Input
                id="fromName"
                value={fromName}
                onChange={(e) => setFromName(e.target.value)}
                placeholder="Your Name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="subject">Subject (optional)</Label>
              <Input
                id="subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Often ignored by SMS gateways"
              />
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <Label htmlFor="body">Message</Label>
                <span className="text-xs text-muted-foreground">
                  {charCount} chars · ~{smsSegments} SMS segment{smsSegments > 1 ? 's' : ''}
                </span>
              </div>
              <Textarea
                id="body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={5}
                required
                placeholder="Your message here…"
              />
            </div>

            {status && (
              <p
                className={`text-sm rounded-md p-2 ${
                  status.type === 'success'
                    ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                    : 'bg-destructive/10 text-destructive'
                }`}
              >
                {status.msg}
              </p>
            )}

            <Button type="submit" disabled={loading || !body.trim()}>
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Send
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Quick gateway reference */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Gateway reference</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-2 text-sm">
            {gateways.slice(0, 12).map((g) => (
              <div key={g.id} className="flex items-center justify-between border-b border-border/40 py-1.5">
                <span>{g.name}</span>
                <div className="flex items-center gap-2">
                  <code className="text-xs">@{g.sms_gateway || g.mms_gateway}</code>
                  <Badge
                    variant={
                      g.status === 'active'
                        ? 'success'
                        : g.status === 'degraded'
                          ? 'warning'
                          : 'destructive'
                    }
                  >
                    {g.status}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
